import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Undo as RestoreIcon,
} from "@mui/icons-material";
import { apiClient } from "../../utils/apiClient";
import type {
  ShipmentApi,
  ManualUpdateShipmentRequest,
  ManualUpdateShipmentResponse,
  ShipmentPropertyNames,
} from "@services/shipments/types";
import { TinyValueComponent } from "../../internal/components/TinyValueComponent";
import { MergeShipmentAction } from "./MergeShipmentAction";
import { AddOrRemoveEmailAction } from "./AddOrRemoveEmailAction";
import { PROPERTY_NAMES } from "./shipmentPropertiesUtils";

interface EditShipmentModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
  onSuccess: () => void;
}

interface PropertyFormData {
  id: string; // temp ID for form management
  name: ShipmentPropertyNames;
  value: string;
  isNew: boolean;
  originalValue?: string; // for modified properties
  isRemoved?: boolean; // for tracking removed existing properties
}

export function EditShipmentModal({
  open,
  onClose,
  shipmentId,
  onSuccess,
}: EditShipmentModalProps) {
  const [shipment, setShipment] = useState<ShipmentApi | null>(null);
  const [properties, setProperties] = useState<PropertyFormData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [togglingValidity, setTogglingValidity] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (open && shipmentId) {
      fetchShipment();
    }
  }, [open, shipmentId]);

  const fetchShipment = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get shipment details - we'll need to get this from the list endpoint
      // for now, but ideally we'd have a get-by-id endpoint
      const response = await apiClient.post<ShipmentApi>(
        "/shipments/get-by-id",
        {
          shipmentId,
        },
      );
      const foundShipment = response;
      if (!foundShipment) {
        throw new Error("Shipment not found");
      }

      setShipment(foundShipment);

      // Convert properties to form data
      const formProperties: PropertyFormData[] = foundShipment.properties.map(
        (prop, index) => ({
          id: `existing-${index}`,
          name: prop.name as ShipmentPropertyNames,
          value: prop.value,
          isNew: false,
          originalValue: prop.value,
        }),
      );

      setProperties(formProperties);
    } catch (err) {
      console.error("Error fetching shipment:", err);
      setError("Failed to load shipment details");
    } finally {
      setLoading(false);
    }
  };

  const addNewProperty = useCallback(() => {
    const newProperty: PropertyFormData = {
      id: `new-${Date.now()}`,
      name: "shipper", // default value
      value: "",
      isNew: true,
    };
    setProperties((prev) => [...prev, newProperty]);
  }, []);

  const removeProperty = useCallback((id: string) => {
    setProperties(
      (prev) =>
        prev
          .map((prop) => {
            if (prop.id === id) {
              if (prop.isNew) {
                // Remove new properties completely
                return null;
              } else {
                // Mark existing properties as removed
                return { ...prop, isRemoved: true };
              }
            }
            return prop;
          })
          .filter(Boolean) as PropertyFormData[],
    );
  }, []);

  const restoreProperty = useCallback((id: string) => {
    setProperties((prev) =>
      prev.map((prop) =>
        prop.id === id ? { ...prop, isRemoved: false } : prop,
      ),
    );
  }, []);

  const updateProperty = useCallback(
    (id: string, field: keyof PropertyFormData, value: any) => {
      setProperties((prev) =>
        prev.map((prop) =>
          prop.id === id ? { ...prop, [field]: value } : prop,
        ),
      );
    },
    [],
  );

  const handleToggleValidity = useCallback(async () => {
    if (!shipment) return;

    try {
      setTogglingValidity(true);
      setError(null);

      const isCurrentlyValid = !shipment.flaggedAsInvalid;
      const newValidityStatus = !isCurrentlyValid;

      await apiClient.post("/shipments/resolve_shipment_error", {
        shipmentId,
        isValid: newValidityStatus,
      });

      // Update local state
      setShipment((prev) =>
        prev ? { ...prev, flaggedAsInvalid: !newValidityStatus } : prev,
      );

      // Refresh the shipment data to get the latest state
      await fetchShipment();
    } catch (err) {
      console.error("Error toggling validity:", err);
      setError("Failed to update shipment validity");
    } finally {
      setTogglingValidity(false);
    }
  }, [shipment, shipmentId]);

  const handleDelete = useCallback(async () => {
    try {
      setDeleting(true);
      setError(null);
      await apiClient.post("/shipments/delete", { shipmentId });
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error deleting shipment:", err);
      setError("Failed to delete shipment");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }, [shipmentId, onSuccess, onClose]);

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setValidationErrors([]);

      // Build the update operation
      const addedProperties = properties
        .filter((prop) => prop.isNew && prop.value.trim() && !prop.isRemoved)
        .map((prop) => ({
          attribute: prop.name,
          newValue: prop.value,
        }));

      const modifiedProperties = properties
        .filter(
          (prop) =>
            !prop.isNew && !prop.isRemoved && prop.value !== prop.originalValue,
        )
        .map((prop) => ({
          attribute: prop.name,
          newValue: prop.value,
          oldValue: prop.originalValue!,
        }));

      const removedProperties = properties
        .filter((prop) => !prop.isNew && prop.isRemoved)
        .map((prop) => ({
          attribute: prop.name,
          oldValue: prop.originalValue!,
        }));

      const request: ManualUpdateShipmentRequest = {
        shipmentId,
        updateOperation: {
          addedProperties,
          modifiedProperties,
          ...(removedProperties.length > 0 && { removedProperties }),
        },
      };

      const response = await apiClient.post<ManualUpdateShipmentResponse>(
        "/shipments/update",
        request,
      );

      if (response.status === "failed") {
        setValidationErrors(response.errors || [response.message]);
        return;
      }

      // Success
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating shipment:", err);
      setError("Failed to update shipment");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = properties.some(
    (prop) =>
      (prop.isNew && prop.value.trim() && !prop.isRemoved) ||
      (!prop.isNew && !prop.isRemoved && prop.value !== prop.originalValue) ||
      (!prop.isNew && prop.isRemoved),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: "80vh" },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Edit Shipment Properties</Typography>
        {shipment && (
          <Typography variant="caption" color="text.secondary">
            Shipment ID: <TinyValueComponent value={shipment.id} clipboard />
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Stack spacing={2}>
            {validationErrors.length > 0 && (
              <Alert severity="error">
                <Typography variant="subtitle2">Validation Errors:</Typography>
                {validationErrors.map((err, index) => (
                  <Typography key={index} variant="body2">
                    • {err}
                  </Typography>
                ))}
              </Alert>
            )}

            <Stack spacing={2}>
              <MergeShipmentAction
                shipmentId={shipmentId}
                tags={shipment?.tags || []}
                onSuccess={onSuccess}
                disabled={loading || saving}
              />
              <AddOrRemoveEmailAction
                shipmentId={shipmentId}
                tags={shipment?.tags || []}
                onSuccess={onSuccess}
                disabled={loading || saving}
              />
            </Stack>

            <Typography variant="h6" sx={{ mt: 2 }}>
              Properties
            </Typography>

            {properties.map((property) => (
              <Stack
                key={property.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  opacity: property.isRemoved ? 0.5 : 1,
                  textDecoration: property.isRemoved ? "line-through" : "none",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Property Name</InputLabel>
                  <Select
                    value={property.name}
                    label="Property Name"
                    onChange={(e) =>
                      updateProperty(property.id, "name", e.target.value)
                    }
                    disabled={!property.isNew || property.isRemoved} // Can't change name of existing properties or removed ones
                  >
                    {PROPERTY_NAMES.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option.replace(/_/g, " ")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Value"
                  value={property.value}
                  onChange={(e) =>
                    updateProperty(property.id, "value", e.target.value)
                  }
                  size="small"
                  sx={{ flex: 1 }}
                  disabled={property.isRemoved}
                />

                {property.isNew && !property.isRemoved && (
                  <IconButton
                    onClick={() => removeProperty(property.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}

                {!property.isNew && !property.isRemoved && (
                  <IconButton
                    onClick={() => removeProperty(property.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}

                {property.isRemoved && (
                  <IconButton
                    onClick={() => restoreProperty(property.id)}
                    size="small"
                    color="primary"
                  >
                    <RestoreIcon />
                  </IconButton>
                )}

                {!property.isNew &&
                  !property.isRemoved &&
                  property.originalValue !== property.value && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ minWidth: 60 }}
                    >
                      Modified
                    </Typography>
                  )}

                {property.isRemoved && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ minWidth: 60 }}
                  >
                    Removed
                  </Typography>
                )}
              </Stack>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addNewProperty}
              variant="outlined"
              sx={{ alignSelf: "flex-start" }}
            >
              Add Property
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        {confirmDelete ? (
          <Stack direction="row" spacing={1} sx={{ mr: "auto" }}>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              size="small"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleting ? "Deleting..." : "Confirm Delete"}
            </Button>
            <Button
              onClick={() => setConfirmDelete(false)}
              size="small"
              disabled={deleting}
            >
              Cancel
            </Button>
          </Stack>
        ) : (
          <Button
            onClick={() => setConfirmDelete(true)}
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            disabled={loading || saving || deleting}
            sx={{ mr: "auto" }}
          >
            Delete Shipment
          </Button>
        )}
        <Button
          onClick={handleToggleValidity}
          disabled={loading || saving || togglingValidity || !shipment}
          color={shipment?.flaggedAsInvalid ? "success" : "warning"}
          variant="outlined"
          size="small"
          startIcon={
            togglingValidity ? (
              <CircularProgress size={16} />
            ) : shipment?.flaggedAsInvalid ? (
              <ValidIcon />
            ) : (
              <InvalidIcon />
            )
          }
        >
          {togglingValidity
            ? "Updating..."
            : shipment?.flaggedAsInvalid
              ? "Mark Valid"
              : "Mark Invalid"}
        </Button>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving || !hasChanges || loading}
        >
          {saving ? <CircularProgress size={20} /> : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
